import React, { useState, useEffect, useRef } from "react";
import { IoIosArrowDown } from "react-icons/io";
import { FiMenu } from "react-icons/fi";
import MobileNav from "./MobileNav";
import logo from "../../assets/images/logo.png";
import { useLocation, useNavigate } from "react-router-dom";
import LaunchingSoonModal from "../popup";

const navItems = [
  { id: "home", label: "Home", link: "" },
  { id: "families", label: "Families", link: "families" },
  { id: "business", label: "Businesses", link: "business" },
  { id: "volunteers", label: "Volunteers", link: "volunteers" },
  { id: "contact", label: "Contact us", link: "contact" },
  {
    id: "explore",
    label: "Explore",
    children: [
      { id: "about", label: "About us", link: "exploreApp" },
      { id: "social", label: "Social Impact", link: "cards" },
      { id: "sponsor", label: "Our Partners", link: "sponsor" },
    ],
  },
];

const Navbar = () => {
  const [isSideMenuOpen, setSideMenu] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const dropdownRef = useRef(null);
  const menuRef = useRef(null);

  const openSideMenu = () => setSideMenu(true);
  const closeSideMenu = () => setSideMenu(false);

  const isActive = (path) => location.pathname === path;

  const isParentActive = (parent) => {
    if (isActive(parent.link)) return true;
    return parent.children?.some((child) => isActive(child.link));
  };

  const handleNavigate = (link) => {
    if (!link) return;

    // Check if the link matches the current page
    if (location.pathname === "/") {
      const section = document.getElementById(link);
      if (section) {
        // Custom slow scroll
        const scrollToElement = () => {
          const targetPosition = section.offsetTop;
          const currentPosition = window.pageYOffset;
          const distance = targetPosition - currentPosition;
          const duration = 500;
          const easing = (t) => t * (2 - t);

          let startTime = null;

          const scrollStep = (timestamp) => {
            if (!startTime) startTime = timestamp;
            const progress = (timestamp - startTime) / duration;
            const easedProgress = easing(progress); // Apply easing
            const scrollPosition = currentPosition + distance * easedProgress;
            window.scrollTo(0, scrollPosition);

            if (progress < 1) {
              requestAnimationFrame(scrollStep);
            } else {
              window.scrollTo(0, targetPosition); // Ensure the final position
            }
          };

          requestAnimationFrame(scrollStep);
        };

        scrollToElement();
      }
    } else {
      navigate(`/${link}`);
    }
  };

  const toggleDropdown = (id) => {
    setOpenDropdownId(openDropdownId === id ? null : id);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Ensure the event target is not inside the dropdown or menu
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) && // Check if the click is not inside the dropdown
        menuRef.current &&
        !menuRef.current.contains(event.target) // Also check if it's not inside the menu
      ) {
        setOpenDropdownId(null); // Close the dropdown
      }
    };

    // Attach the event listener to detect clicks outside
    document.addEventListener("mousedown", handleClickOutside);

    // Cleanup the event listener on component unmount or dependency change
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []); // Empty dependency array ensures this only runs on mount and unmount

  const handleDropdownLinkClick = (link) => {
    setOpenDropdownId(null);
    handleNavigate(link);
  };

  return (
    <div className="max-w-[1280px] mx-auto px-5 py-2 flex items-center justify-between">
      {/* Left Section */}
      <div className="font-bold text-lg">
        <img className="cursor-pointer w-36" src={logo} alt="logo" />
      </div>
      <section className="flex items-center gap-10">
        {/* Logo */}
        {isSideMenuOpen && <MobileNav closeSideMenu={closeSideMenu} />}

        {/* Navigation Links */}
        <div className="hidden md:flex items-center gap-4 transition-all">
          {navItems.map((item) => (
            <div
              key={item.id}
              className="relative group px-2 py-2 transition-all"
            >
              <button
                onClick={() => {
                  if (item.children) {
                    toggleDropdown(item.id);
                  } else {
                    handleNavigate(item.link);
                  }
                }}
                className={`flex cursor-pointer text-darkText text-base items-center gap-2 ${
                  isParentActive(item)
                    ? "bg-secondary px-4 py-2 text-white rounded-md"
                    : "text-primaryText"
                } group relative`}
              >
                <span>{item.label}</span>
                {item.children && (
                  <IoIosArrowDown
                    className={`transition-all ${
                      openDropdownId === item.id ? "rotate-180" : "rotate-0"
                    }`}
                  />
                )}
                <span
                  className={`absolute bottom-0 left-0 w-0 h-[2px] bg-secondary transition-all duration-300 group-hover:w-full ${
                    isParentActive(item) ? "hidden" : ""
                  }`}
                ></span>
              </button>

              {/* Dropdown */}
              {item.children && openDropdownId === item.id && (
                <div
                  ref={dropdownRef}
                  className="absolute left-0 top-12 z-10 w-auto whitespace-nowrap flex-col gap-1 rounded-lg bg-white py-3 shadow-md transition-all"
                >
                  {item.children.map((child) => (
                    <button
                      key={child.id}
                      onClick={() => handleDropdownLinkClick(child.link)}
                      className={`flex cursor-pointer text-darkText items-center py-1 pl-6 pr-8 ${
                        isActive(child.link)
                          ? "text-secondaryText"
                          : "text-primaryText"
                      } hover:text-secondaryText`}
                    >
                      {child.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        <div>
         <a
            href="https://web.dev.learnlocal.app/auth/login"
            className="hidden md:flex bg-secondary text-primaryText px-2 
             rounded-md transition-all hover:bg-secondaryDark"
          >
            Login
          </a> 
          {/* <button
            className="hidden md:flex bg-secondary text-primaryText px-2 "
            onClick={() => setShowModal(true)}
          >
            Login
          </button> */}
        </div>
      </section>

      {/* Mobile Menu Icon */}
      <FiMenu
        onClick={openSideMenu}
        className="cursor-pointer text-4xl md:hidden"
      />
      <LaunchingSoonModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
      />
    </div>
  );
};

export default Navbar;
